<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <Form ref="pricingRuleForm" @submit="editPricingRule" :devtools="{ enabled: false }">
        <div class="row">

          <!-- ===== ÉTAPE 1 : INFORMATIONS DE BASE ===== -->
          <div class="col-md-12 mb-3">
            <div class="alert alert-primary">
              <strong>Étape 1 :</strong> Informations de base de la règle
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Code <span class="text-danger">*</span>
              </label>
              <Field name="code" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Ex: BESC-BJ-DOSSIER" v-model="formData.code" />
              <ErrorMessage name="code" class="text-danger" />
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Nom <span class="text-danger">*</span>
              </label>
              <Field name="name" type="text" class="form-control shadow-none fs-md-15 text-black"
                placeholder="Ex: Frais de dossier" v-model="formData.name" />
              <ErrorMessage name="name" class="text-danger" />
            </div>
          </div>

          <div class="col-md-4">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Zone Tarifaire <span class="text-danger">*</span>
              </label>
              <Field name="tariffZone" v-slot="{ handleChange }">
                <Multiselect 
                  :options="tariffZoneOptions" 
                  :searchable="true" 
                  :object="true"
                  track-by="value" 
                  label="label"
                  :modelValue="formData.tariffZone"
                  @update:modelValue="(val) => { formData.tariffZone = val; handleChange(val); }"
                  placeholder="Sélectionner la zone" 
                />
              </Field>
              <ErrorMessage name="tariffZone" class="text-danger" />
            </div>
          </div>

          <!-- ===== ÉTAPE 2 : TYPE D'ENTITÉ ===== -->
          <div class="col-md-12 mt-3 mb-3">
            <div class="alert alert-info">
              <strong>Étape 2 :</strong> Sur quel élément cette règle doit-elle s'appliquer ?
            </div>
          </div>

          <div class="col-md-12">
            <div class="form-group mb-15 mb-sm-20 mb-md-25">
              <label class="d-block text-black fw-semibold mb-10">
                Type d'entité <span class="text-danger">*</span>
              </label>
              <Field name="appliesToEntityType" v-slot="{ handleChange }">
                <Multiselect 
                  :options="entityTypeOptions" 
                  :searchable="false" 
                  :object="true"
                  track-by="value" 
                  label="label"
                  :modelValue="formData.appliesToEntityType"
                  @update:modelValue="(val) => { 
                    formData.appliesToEntityType = val; 
                    handleChange(val);
                    onEntityTypeChange(val);
                  }" 
                  placeholder="Choisissez le type d'entité..." 
                />
              </Field>
              <ErrorMessage name="appliesToEntityType" class="text-danger" />
            </div>
          </div>

          <!-- Message d'aide -->
          <div class="col-md-12" v-if="selectedEntityType">
            <div class="alert alert-success mb-4">
              <strong>{{ entityTypeHelp.title }}</strong>
              <p class="mb-0 mt-2">{{ entityTypeHelp.description }}</p>
            </div>
          </div>

          <!-- ===== ÉTAPE 3 : CONFIGURATION PRINCIPALE (obligatoire) ===== -->
          <template v-if="selectedEntityType">
            <div class="col-md-12 mt-3 mb-3">
              <div class="alert alert-warning">
                <strong>Étape 3 :</strong> Configuration principale de la règle (obligatoire)
              </div>
            </div>

            <!-- ========== BESC ========== -->
            <template v-if="selectedEntityType === 'BESC'">
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Montant fixe par dossier <span class="text-danger">*</span>
                  </label>
                  <Field name="baseAmount" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 50000"
                    v-model="formData.baseAmount" />
                  <ErrorMessage name="baseAmount" class="text-danger" />
                  <small class="text-muted">Ce montant sera facturé une seule fois par BESC</small>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Devise <span class="text-danger">*</span>
                  </label>
                  <Field name="amountCurrency" v-slot="{ handleChange }">
                    <Multiselect :options="currencyOptions" :searchable="true" track-by="value" label="label"
                      :modelValue="formData.amountCurrency" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.amountCurrency = val; handleChange(val); }"
                      placeholder="Sélectionner la devise" />
                  </Field>
                  <ErrorMessage name="amountCurrency" class="text-danger" />
                </div>
              </div>
            </template>

            <!-- ========== BL ========== -->
            <template v-if="selectedEntityType === 'BL'">
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Montant par BL <span class="text-danger">*</span>
                  </label>
                  <Field name="baseAmount" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 16437"
                    v-model="formData.baseAmount" />
                  <ErrorMessage name="baseAmount" class="text-danger" />
                  <small class="text-muted">Ce montant sera multiplié par le nombre de BL</small>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Devise <span class="text-danger">*</span>
                  </label>
                  <Field name="amountCurrency" v-slot="{ handleChange }">
                    <Multiselect :options="currencyOptions" :searchable="true" track-by="value" label="label"
                      :modelValue="formData.amountCurrency" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.amountCurrency = val; handleChange(val); }"
                      placeholder="Sélectionner la devise" />
                  </Field>
                  <ErrorMessage name="amountCurrency" class="text-danger" />
                </div>
              </div>
            </template>

            <!-- ========== CONTAINER ========== -->
            <template v-if="selectedEntityType === 'CONTAINER'">
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Montant par conteneur <span class="text-danger">*</span>
                  </label>
                  <Field name="perContainerAmount" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 3287"
                    v-model="formData.perContainerAmount" />
                  <ErrorMessage name="perContainerAmount" class="text-danger" />
                  <small class="text-muted">Montant × nombre de conteneurs</small>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Devise <span class="text-danger">*</span>
                  </label>
                  <Field name="amountCurrency" v-slot="{ handleChange }">
                    <Multiselect :options="currencyOptions" :searchable="true" track-by="value" label="label"
                      :modelValue="formData.amountCurrency" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.amountCurrency = val; handleChange(val); }"
                      placeholder="Sélectionner la devise" />
                  </Field>
                  <ErrorMessage name="amountCurrency" class="text-danger" />
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Types de conteneur <span class="text-danger">*</span>
                  </label>
                  <Field name="metadata.containerTypes" v-slot="{ handleChange }">
                    <Multiselect :options="containerTypeOptions" :searchable="true" mode="tags" track-by="value"
                      label="label" :modelValue="formData.containerTypes" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.containerTypes = val; handleChange(val); }"
                      placeholder="Sélectionner un ou plusieurs types" />
                  </Field>
                  <ErrorMessage name="metadata.containerTypes" class="text-danger" />
                  <small class="text-muted">Obligatoire : choisissez les types concernés</small>
                </div>
              </div>

              <div class="col-md-4 d-flex align-items-center">
                <div class="form-group mb-15 mb-sm-20 mb-md-25 w-100">
                  <div class="form-check">
                    <Field name="metadata.requireDistantOrigin" type="checkbox" class="form-check-input"
                      id="requireDistantOrigin" :value="true" v-model="formData.requireDistantOrigin" />
                    <label class="form-check-label text-black fw-semibold" for="requireDistantOrigin">
                      Uniquement pour les origines lointaines (Asie, Amérique, Océanie)
                    </label>
                  </div>
                  <small class="text-muted d-block mt-2">
                    Si coché, cette règle ne s'appliquera que si le port de chargement est en Asie, Amérique ou Océanie
                  </small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Date de début <span class="text-danger">*</span>
                  </label>
                  <Field name="effectiveFrom" type="date" class="form-control shadow-none fs-md-15 text-black"
                    v-model="formData.effectiveFrom" />
                  <ErrorMessage name="effectiveFrom" class="text-danger" />
                  <small class="text-muted">Laissez vide pour "immédiat"</small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Priorité <span class="text-danger">*</span>
                  </label>
                  <Field name="priority" v-slot="{ handleChange }">
                    <Multiselect :options="priorityOptions" :searchable="false" track-by="value" label="label"
                      :modelValue="formData.priority" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.priority = val; handleChange(val); }"
                      placeholder="Sélectionner la priorité" />
                  </Field>
                  <ErrorMessage name="priority" class="text-danger" />
                  <small class="text-muted">Plus le nombre est bas, plus prioritaire</small>
                </div>
              </div>
            </template>

            <!-- ========== CARGO_LINE ========== -->
            <template v-if="selectedEntityType === 'CARGO_LINE'">
              <div class="col-md-12 mb-3">
                <div class="alert alert-danger">
                  <strong>Important :</strong> La catégorie de produit est OBLIGATOIRE pour ce type de règle afin
                  d'éviter la double facturation !
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Catégorie de produit <span class="text-danger">*</span>
                  </label>
                  <Field name="categorieProduit" v-slot="{ handleChange }">
                    <Multiselect :options="categorieProduitOptions" :searchable="true" track-by="value" label="label"
                      :modelValue="formData.categorieProduit" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.categorieProduit = val; handleChange(val); }"
                      placeholder="Ex: Véhicules" />
                  </Field>
                  <ErrorMessage name="categorieProduit" class="text-danger" />
                  <small class="text-muted">Seules les lignes de cette catégorie seront comptées</small>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Montant par ligne <span class="text-danger">*</span>
                  </label>
                  <Field name="baseAmount" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 16387"
                    v-model="formData.baseAmount" />
                  <ErrorMessage name="baseAmount" class="text-danger" />
                  <small class="text-muted">Montant × nombre de lignes de la catégorie</small>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Devise <span class="text-danger">*</span>
                  </label>
                  <Field name="amountCurrency" v-slot="{ handleChange }">
                    <Multiselect :options="currencyOptions" :searchable="true" track-by="value" label="label"
                      :modelValue="formData.amountCurrency" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.amountCurrency = val; handleChange(val); }"
                      placeholder="Sélectionner la devise" />
                  </Field>
                  <ErrorMessage name="amountCurrency" class="text-danger" />
                </div>
              </div>
            </template>

            <!-- ========== GOODS ========== -->
            <template v-if="selectedEntityType === 'GOODS'">
              <div class="col-md-12 mb-3">
                <div class="alert alert-info">
                  <strong>Marchandises avec conditionnement</strong>
                  <p class="mb-0 mt-2">
                    Sélectionnez le type de conditionnement pour appliquer la grille tarifaire correspondante
                  </p>
                </div>
              </div>

              <!-- Type de conditionnement -->
              <div class="col-md-12">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Type de conditionnement <span class="text-danger">*</span>
                  </label>
                  <Field name="packagingType" v-slot="{ handleChange }">
                    <Multiselect :options="packagingTypeOptions" :searchable="false" track-by="value" label="label"
                      :modelValue="formData.packagingType" value-prop="value" :object="true" @update:modelValue="(val) => {
                        formData.packagingType = val;
                        handleChange(val);
                        onPackagingTypeChange(val);
                      }" placeholder="Choisissez le type de conditionnement..." />
                  </Field>
                  <ErrorMessage name="packagingType" class="text-danger" />
                </div>
              </div>

              <!-- SI CONDITIONNEMENT SÉLECTIONNÉ -->
              <template v-if="selectedPackagingType">
                <!-- ========== FCL ========== -->
                <template v-if="selectedPackagingType === 'FCL'">
                  <div class="col-md-12 mb-2">
                    <div class="alert alert-success">
                      <strong>Tarification FCL (Conteneurs pleins)</strong>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Taille de la tranche <span class="text-danger">*</span>
                      </label>
                      <Field name="trancheSize" type="number" class="form-control" placeholder="5"
                        v-model="formData.trancheSize" />
                      <ErrorMessage name="trancheSize" class="text-danger" />
                      <small class="text-muted">Nombre de conteneurs par tranche (ex: 5)</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Unité de mesure <span class="text-danger">*</span>
                      </label>
                      <Field name="unitOfMeasure" v-slot="{ handleChange }">
                        <Multiselect 
                          :options="unitOfMeasureOptions" 
                          :searchable="true" 
                          :object="true"
                          track-by="value" 
                          label="label"
                          v-model="formData.unitOfMeasure"
                          @update:modelValue="handleChange"
                        />
                      </Field>
                      <ErrorMessage name="unitOfMeasure" class="text-danger" />
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">Max par ECTN</label>
                      <Field name="maxPerEctn" type="number" class="form-control" placeholder="Illimité"
                        v-model="formData.maxPerEctn" />
                      <ErrorMessage name="maxPerEctn" class="text-danger" />
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Tarif Europe/Afrique <span class="text-danger">*</span>
                      </label>
                      <Field name="amountForLocalOrigin" type="number" step="0.01" class="form-control" placeholder="25"
                        v-model="formData.amountForLocalOrigin" />
                      <ErrorMessage name="amountForLocalOrigin" class="text-danger" />
                      <small class="text-muted">Par tranche de {{ formData.trancheSize || 5 }} conteneurs</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Tarif Autres origines <span class="text-danger">*</span>
                      </label>
                      <Field name="amountForDistantOrigin" type="number" step="0.01" class="form-control"
                        placeholder="100" v-model="formData.amountForDistantOrigin" />
                      <ErrorMessage name="amountForDistantOrigin" class="text-danger" />
                      <small class="text-muted">Asie, Amérique, Océanie</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Devise <span class="text-danger">*</span>
                      </label>
                      <Field name="amountCurrency" v-slot="{ handleChange }">
                        <Multiselect :options="currencyOptions" :searchable="true" track-by="value" label="label"
                          :modelValue="formData.amountCurrency" value-prop="value" :object="true"
                          @update:modelValue="(val) => { formData.amountCurrency = val; handleChange(val); }" />
                      </Field>
                      <ErrorMessage name="amountCurrency" class="text-danger" />
                    </div>
                  </div>
                </template>

                <!-- ========== ROULANT LÉGER / LOURD ========== -->
                <template v-if="selectedPackagingType === 'ROULANT_LEGER' || selectedPackagingType === 'ROULANT_LOURD'">
                  <div class="col-md-12 mb-2">
                    <div class="alert alert-success">
                      <strong>Tarification Roulant {{ selectedPackagingType === 'ROULANT_LEGER' ? 'Léger (< 5t)'
                        : 'Lourd (≥ 5t)' }}</strong>
                    </div>
                  </div>

                  <div class="col-md-3">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Poids minimum (tonnes) <span class="text-danger">*</span>
                      </label>
                      <Field name="minWeight" type="number" step="0.01" class="form-control"
                        :placeholder="selectedPackagingType === 'ROULANT_LEGER' ? '0' : '5'"
                        v-model="formData.minWeight" />
                      <ErrorMessage name="minWeight" class="text-danger" />
                    </div>
                  </div>

                  <div class="col-md-3">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Poids maximum (tonnes) <span class="text-danger">*</span>
                      </label>
                      <Field name="maxWeight" type="number" step="0.01" class="form-control"
                        :placeholder="selectedPackagingType === 'ROULANT_LEGER' ? '4.99' : '999'"
                        v-model="formData.maxWeight" />
                      <ErrorMessage name="maxWeight" class="text-danger" />
                    </div>
                  </div>

                  <div class="col-md-3">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Unité de mesure <span class="text-danger">*</span>
                      </label>
                      <Field name="unitOfMeasure" v-slot="{ handleChange }">
                        <Multiselect :options="unitOfMeasureOptions" :searchable="true" track-by="value" label="label"
                          :modelValue="formData.unitOfMeasure" value-prop="value" :object="true"
                          @update:modelValue="(val) => { formData.unitOfMeasure = val; handleChange(val); }" />
                      </Field>
                      <ErrorMessage name="unitOfMeasure" class="text-danger" />
                    </div>
                  </div>

                  <div class="col-md-3">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Max par ECTN <span class="text-danger">*</span>
                      </label>
                      <Field name="maxPerEctn" type="number" class="form-control" placeholder="1"
                        v-model="formData.maxPerEctn" />
                      <ErrorMessage name="maxPerEctn" class="text-danger" />
                      <small class="text-muted">Ex: 1 véhicule maximum par ECTN</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Tarif Europe/Afrique <span class="text-danger">*</span>
                      </label>
                      <Field name="amountForLocalOrigin" type="number" step="0.01" class="form-control" placeholder="25"
                        v-model="formData.amountForLocalOrigin" />
                      <ErrorMessage name="amountForLocalOrigin" class="text-danger" />
                      <small class="text-muted">Par véhicule</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Tarif Autres origines <span class="text-danger">*</span>
                      </label>
                      <Field name="amountForDistantOrigin" type="number" step="0.01" class="form-control"
                        placeholder="25" v-model="formData.amountForDistantOrigin" />
                      <ErrorMessage name="amountForDistantOrigin" class="text-danger" />
                      <small class="text-muted">Asie, Amérique, Océanie</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Devise <span class="text-danger">*</span>
                      </label>
                      <Field name="amountCurrency" v-slot="{ handleChange }">
                        <Multiselect :options="currencyOptions" :searchable="true" track-by="value" label="label"
                          :modelValue="formData.amountCurrency" value-prop="value" :object="true"
                          @update:modelValue="(val) => { formData.amountCurrency = val; handleChange(val); }" />
                      </Field>
                      <ErrorMessage name="amountCurrency" class="text-danger" />
                    </div>
                  </div>
                </template>

                <!-- ========== GROUPAGE / CONVENTIONNEL / VRAC ========== -->
                <template v-if="['GROUPAGE', 'CONVENTIONNEL', 'VRAC_COMPLET'].includes(selectedPackagingType)">
                  <div class="col-md-12 mb-2">
                    <div class="alert alert-success">
                      <strong>Tarification {{
                        selectedPackagingType === 'GROUPAGE' ? 'Groupage' :
                          selectedPackagingType === 'CONVENTIONNEL' ? 'Conventionnel/Vrac' :
                            'Vrac navire complet'
                      }}</strong>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Taille de la tranche (m/t) <span class="text-danger">*</span>
                      </label>
                      <Field name="trancheSize" type="number" class="form-control" placeholder="300"
                        v-model="formData.trancheSize" />
                      <ErrorMessage name="trancheSize" class="text-danger" />
                      <small class="text-muted">Tonnes métriques par tranche (ex: 300)</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Unité de mesure <span class="text-danger">*</span>
                      </label>
                      <Field name="unitOfMeasure" v-slot="{ handleChange }">
                        <Multiselect :options="unitOfMeasureOptions" :searchable="true" track-by="value" label="label"
                          :modelValue="formData.unitOfMeasure" value-prop="value" :object="true"
                          @update:modelValue="(val) => { formData.unitOfMeasure = val; handleChange(val); }" />
                      </Field>
                      <ErrorMessage name="unitOfMeasure" class="text-danger" />
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Max par ECTN (m/t) <span class="text-danger">*</span>
                      </label>
                      <Field name="maxPerEctn" type="number" class="form-control" placeholder="300"
                        v-model="formData.maxPerEctn" />
                      <ErrorMessage name="maxPerEctn" class="text-danger" />
                      <small class="text-muted">Ex: 300 tonnes maximum par ECTN</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Tarif Europe/Afrique <span class="text-danger">*</span>
                      </label>
                      <Field name="amountForLocalOrigin" type="number" step="0.01" class="form-control" placeholder="25"
                        v-model="formData.amountForLocalOrigin" />
                      <ErrorMessage name="amountForLocalOrigin" class="text-danger" />
                      <small class="text-muted">Par tranche de {{ formData.trancheSize || 300 }} m/t</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Tarif Autres origines <span class="text-danger">*</span>
                      </label>
                      <Field name="amountForDistantOrigin" type="number" step="0.01" class="form-control"
                        placeholder="100" v-model="formData.amountForDistantOrigin" />
                      <ErrorMessage name="amountForDistantOrigin" class="text-danger" />
                      <small class="text-muted">Asie, Amérique, Océanie</small>
                    </div>
                  </div>

                  <div class="col-md-4">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Devise <span class="text-danger">*</span>
                      </label>
                      <Field name="amountCurrency" v-slot="{ handleChange }">
                        <Multiselect :options="currencyOptions" :searchable="true" track-by="value" label="label"
                          :modelValue="formData.amountCurrency" value-prop="value" :object="true"
                          @update:modelValue="(val) => { formData.amountCurrency = val; handleChange(val); }" />
                      </Field>
                      <ErrorMessage name="amountCurrency" class="text-danger" />
                    </div>
                  </div>
                </template>
              </template>
            </template>

            <!-- ========== VALUE_BRACKET ========== -->
            <template v-if="selectedEntityType === 'VALUE_BRACKET'">
              <div class="col-md-12 mb-3">
                <div class="alert alert-info">
                  <strong>Tranche de valeur :</strong> Cette règle ne s'appliquera que si la valeur totale du BESC est dans la tranche définie (toujours en EUROS)
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Valeur minimum (EUROS) <span class="text-danger">*</span>
                  </label>
                  <Field name="minDeclaredValue" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 0"
                    v-model="formData.minDeclaredValue" />
                  <ErrorMessage name="minDeclaredValue" class="text-danger" />
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Valeur maximum (EUROS) <span class="text-danger">*</span>
                  </label>
                  <Field name="maxDeclaredValue" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 10000000"
                    v-model="formData.maxDeclaredValue" />
                  <ErrorMessage name="maxDeclaredValue" class="text-danger" />
                  <small class="text-muted">Laissez vide ou mettez un très grand nombre pour "illimité"</small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Taux % <span class="text-danger">*</span>
                  </label>
                  <Field name="percentageRate" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 0.05"
                    v-model="formData.percentageRate" />
                  <ErrorMessage name="percentageRate" class="text-danger" />
                  <small class="text-muted">% de la valeur déclarée</small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    OU Montant fixe (EUROS)
                  </label>
                  <Field name="baseAmount" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 10000"
                    v-model="formData.baseAmount" />
                  <ErrorMessage name="baseAmount" class="text-danger" />
                  <small class="text-muted">Si renseigné, ce montant fixe sera appliqué au lieu du pourcentage</small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Date de début <span class="text-danger">*</span>
                  </label>
                  <Field name="effectiveFrom" type="date" class="form-control shadow-none fs-md-15 text-black"
                    v-model="formData.effectiveFrom" />
                  <ErrorMessage name="effectiveFrom" class="text-danger" />
                  <small class="text-muted">Laissez vide pour "immédiat"</small>
                </div>
              </div>

              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Priorité <span class="text-danger">*</span>
                  </label>
                  <Field name="priority" v-slot="{ handleChange }">
                    <Multiselect :options="priorityOptions" :searchable="false" track-by="value" label="label"
                      :modelValue="formData.priority" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.priority = val; handleChange(val); }"
                      placeholder="Sélectionner la priorité" />
                  </Field>
                  <ErrorMessage name="priority" class="text-danger" />
                  <small class="text-muted">Plus le nombre est bas, plus prioritaire</small>
                </div>
              </div>
            </template>

            <!-- Champs communs Date de début et Priorité -->
            <template v-if="!['GOODS', 'VALUE_BRACKET', 'CONTAINER'].includes(selectedEntityType)">
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Date de début <span class="text-danger">*</span>
                  </label>
                  <Field name="effectiveFrom" type="date" class="form-control shadow-none fs-md-15 text-black"
                    v-model="formData.effectiveFrom" />
                  <ErrorMessage name="effectiveFrom" class="text-danger" />
                  <small class="text-muted">Laissez vide pour "immédiat"</small>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Priorité <span class="text-danger">*</span>
                  </label>
                  <Field name="priority" v-slot="{ handleChange }">
                    <Multiselect :options="priorityOptions" :searchable="false" track-by="value" label="label"
                      :modelValue="formData.priority" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.priority = val; handleChange(val); }"
                      placeholder="Sélectionner la priorité" />
                  </Field>
                  <ErrorMessage name="priority" class="text-danger" />
                  <small class="text-muted">Plus le nombre est bas, plus prioritaire</small>
                </div>
              </div>
            </template>
          </template>

          <!-- ===== ÉTAPE 4 : OPTIONS AVANCÉES (facultatif) ===== -->
          <template v-if="selectedEntityType">
            <div class="col-md-12 mt-4 mb-3">
              <div class="alert alert-secondary">
                <strong>Étape 4 :</strong> Options avancées (facultatif)
              </div>
            </div>

            <template v-if="!['VALUE_BRACKET', 'BESC', 'BL'].includes(selectedEntityType)">
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Régions du port de chargement
                  </label>
                  <Field name="metadata.loadingPortRegion" v-slot="{ handleChange }">
                    <Multiselect :options="regionOptions" :searchable="false" mode="tags"
                      :modelValue="formData.loadingPortRegion" value-prop="value" :object="true"
                      @update:modelValue="(val) => { formData.loadingPortRegion = val; handleChange(val); }"
                      placeholder="Toutes les régions" />
                  </Field>
                  <small class="text-muted">La règle ne s'appliquera que si le port de chargement est dans ces
                    régions</small>
                </div>
              </div>
            </template>

            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Date de fin</label>
                <Field name="effectiveTo" type="date" class="form-control shadow-none fs-md-15 text-black"
                  v-model="formData.effectiveTo" />
                <ErrorMessage name="effectiveTo" class="text-danger" />
                <small class="text-muted">Laissez vide pour "sans limite"</small>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Régime douanier (optionnel)</label>
                <Field name="regimedouanier" v-slot="{ handleChange }">
                  <Multiselect :options="regimeDouanierOptions" :searchable="true" track-by="value" label="label"
                    :modelValue="formData.regimedouanier" value-prop="value" :object="true"
                    @update:modelValue="(val) => { formData.regimedouanier = val; handleChange(val); }"
                    placeholder="Sélectionner un régime" />
                </Field>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Montant min facturé</label>
                <Field name="minFeeAmount" type="number" step="0.01"
                  class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 1000"
                  v-model="formData.minFeeAmount" />
                <small class="text-muted">Plafond minimum</small>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Montant max facturé</label>
                <Field name="maxFeeAmount" type="number" step="0.01"
                  class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 100000"
                  v-model="formData.maxFeeAmount" />
                <small class="text-muted">Plafond maximum</small>
              </div>
            </div>
            <div class="col-md-12">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <div class="d-flex flex-wrap gap-5 align-items-center">
                  <div class="form-check">
                    <Field name="isExemptionRule" type="checkbox" class="form-check-input" id="isExemptionRule"
                      :value="true" v-model="formData.isExemptionRule" />
                    <label class="form-check-label text-black fw-semibold" for="isExemptionRule">
                      Règle d'exemption (réduction)
                    </label>
                  </div>
                  <div class="form-check">
                    <Field name="isActive" type="checkbox" class="form-check-input" id="isActive" :value="true"
                      v-model="formData.isActive" />
                    <label class="form-check-label text-black fw-semibold" for="isActive">
                      Activer cette règle immédiatement
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <template v-if="formData.isExemptionRule">
              <div class="col-md-4">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Taux d'exemption (%)
                  </label>
                  <Field name="exemptionRate" type="number" step="0.01"
                    class="form-control shadow-none fs-md-15 text-black" placeholder="Ex: 10"
                    v-model="formData.exemptionRate" />
                  <ErrorMessage name="exemptionRate" class="text-danger" />
                  <small class="text-muted">% de réduction</small>
                </div>
              </div>
            </template>

            <div class="col-md-12">
              <div class="form-group mb-15 mb-sm-20 mb-md-25">
                <label class="d-block text-black fw-semibold mb-10">Description</label>
                <Field name="description" as="textarea" rows="3" class="form-control shadow-none fs-md-15 text-black"
                  placeholder="Décrivez cette règle en détail..." v-model="formData.description" />
              </div>
            </div>

            <div class="col-md-12 mt-4">
              <div class="d-flex align-items-center">
                <button type="submit"
                  class="default-btn me-20 transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16 bg-success">
                  Modifier la règle
                </button>
                <router-link to="/listeBescPricingRule"
                  class="btn btn-danger transition border-0 lh-1 fw-medium pt-10 pb-10 ps-25 pe-25">
                  Annuler
                </router-link>
              </div>
            </div>
          </template>

        </div>
      </Form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, reactive, nextTick } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import { error, success } from '@/utils/utils';
import { useRouter, useRoute } from "vue-router";
import ApiService from '@/services/ApiService';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: "EditBescPricingRule",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect
  },
  setup: () => {
    const router = useRouter();
    const route = useRoute();
    const ruleId = route.params.id as string;

    interface Option {
      value: number | string;
      label: string;
    }

    const selectedEntityType = ref<string | null>(null);
    const pricingRuleForm = ref<any | null>(null);
    const containerTypeOptions = ref<Option[]>([]);

    const entityTypeHelp = computed(() => {
      const helps: Record<string, { title: string; description: string }> = {
        'BESC': {
          title: 'BESC - Frais de dossier',
          description: 'Un montant fixe sera appliqué une seule fois par dossier BESC, quel que soit son contenu.'
        },
        'BL': {
          title: 'BL - Frais par connaissement',
          description: 'Le montant sera multiplié par le nombre de BL (connaissements) dans le BESC.'
        },
        'CONTAINER': {
          title: 'CONTAINER - Frais par conteneur',
          description: 'Le montant sera multiplié par le nombre de conteneurs.'
        },
        'CARGO_LINE': {
          title: 'CARGO_LINE - Frais par ligne de marchandise',
          description: 'IMPORTANT : Vous DEVEZ choisir une catégorie de produit pour éviter de facturer toutes les lignes ! Le montant sera multiplié uniquement par les lignes de cette catégorie.'
        },
        'GOODS': {
          title: 'GOODS - Frais sur marchandises',
          description: 'La règle s\'appliquera uniquement aux marchandises d\'une catégorie spécifique. Vous pouvez utiliser un montant fixe par marchandise OU un pourcentage sur leur valeur totale.'
        },
        'VALUE_BRACKET': {
          title: 'VALUE_BRACKET - Frais par tranche de valeur',
          description: 'La règle ne s\'appliquera que si la valeur totale du BESC est dans la tranche définie (toujours en EUROS). Utile pour les taxes progressives.'
        }
      };
      const entityTypeValue = selectedEntityType.value;
      // Gérer le cas où selectedEntityType peut être un objet ou une string
      const entityKey = typeof entityTypeValue === 'object' && (entityTypeValue as any)?.value 
        ? (entityTypeValue as any).value 
        : entityTypeValue;
        
      console.log('entityTypeHelp computed - selectedEntityType.value:', entityTypeValue);
      console.log('entityTypeHelp computed - entityKey extrait:', entityKey);
      console.log('entityTypeHelp computed - helps[entityKey]:', entityKey ? helps[entityKey] : 'pas de valeur');
      
      return entityKey && helps[entityKey] ? helps[entityKey] : { title: '', description: '' };
    });

    const selectedPackagingType = ref<string | null>(null);

    const packagingTypeOptions = [
      { value: 'FCL', label: ' FCL - Conteneurs pleins' },
      { value: 'ROULANT_LEGER', label: ' Roulant Léger (< 5 tonnes)' },
      { value: 'ROULANT_LOURD', label: ' Roulant Lourd (≥ 5 tonnes)' },
      { value: 'GROUPAGE', label: ' Groupage' },
      { value: 'CONVENTIONNEL', label: ' Conventionnel/Vrac' },
      { value: 'VRAC_COMPLET', label: ' Vrac navire complet' }
    ];

    const unitOfMeasureOptions = [
      { value: 'CONTENEUR', label: 'Conteneur(s)' },
      { value: 'VEHICULE', label: 'Véhicule(s)' },
      { value: 'TONNE_METRIQUE', label: 'Tonne(s) métrique(s)' }
    ];

    const onPackagingTypeChange = (value: any) => {
      const packagingValue = value?.value || value;
      selectedPackagingType.value = packagingValue;

      // Pré-remplir selon le type
      if (packagingValue === 'FCL') {
        formData.trancheSize = formData.trancheSize || 5;
        const unitObj = unitOfMeasureOptions.find((opt: any) => opt.value === 'CONTENEUR') || null;
        formData.unitOfMeasure = formData.unitOfMeasure || unitObj;
      } else if (packagingValue === 'ROULANT_LEGER') {
        formData.minWeight = formData.minWeight || 0;
        formData.maxWeight = formData.maxWeight || 4.99;
        const unitObj = unitOfMeasureOptions.find((opt: any) => opt.value === 'VEHICULE') || null;
        formData.unitOfMeasure = formData.unitOfMeasure || unitObj;
        formData.maxPerEctn = formData.maxPerEctn || 1;
      } else if (packagingValue === 'ROULANT_LOURD') {
        formData.minWeight = formData.minWeight || 5;
        formData.maxWeight = formData.maxWeight || 999;
        const unitObj = unitOfMeasureOptions.find((opt: any) => opt.value === 'VEHICULE') || null;
        formData.unitOfMeasure = formData.unitOfMeasure || unitObj;
        formData.maxPerEctn = formData.maxPerEctn || 1;
      } else if (['GROUPAGE', 'CONVENTIONNEL', 'VRAC_COMPLET'].includes(packagingValue)) {
        formData.trancheSize = formData.trancheSize || 300;
        const unitObj = unitOfMeasureOptions.find((opt: any) => opt.value === 'TONNE_METRIQUE') || null;
        formData.unitOfMeasure = formData.unitOfMeasure || unitObj;
        formData.maxPerEctn = formData.maxPerEctn || 300;
      }
    };

    const onEntityTypeChange = (value: any) => {
      const entityValue = value?.value || value;
      selectedEntityType.value = entityValue;
      
      console.log('onEntityTypeChange - value reçu:', value);
      console.log('onEntityTypeChange - entityValue extrait:', entityValue);
      console.log('onEntityTypeChange - selectedEntityType.value assigné:', selectedEntityType.value);

      if (pricingRuleForm.value) {
        const form = pricingRuleForm.value;
        
        // Reset les champs spécifiques selon le type
        form.setFieldValue('baseAmount', formData.baseAmount || null);
        form.setFieldValue('perContainerAmount', formData.perContainerAmount || null);
        form.setFieldValue('percentageRate', formData.percentageRate || null);
        form.setFieldValue('minDeclaredValue', formData.minDeclaredValue || null);
        form.setFieldValue('maxDeclaredValue', formData.maxDeclaredValue || null);
      }
    };

    // Pas de validation Yup nécessaire
    const pricingRuleSchema = null;

    const tariffZoneOptions = ref<Option[]>([]);
    const categorieProduitOptions = ref<Option[]>([]);
    const regimeDouanierOptions = ref<Option[]>([]);

    const entityTypeOptions = [
      { value: 'BESC', label: 'BESC - Dossier complet' },
      { value: 'BL', label: 'BL - Connaissement' },
      { value: 'CONTAINER', label: 'CONTAINER - Conteneur' },
      { value: 'CARGO_LINE', label: 'CARGO_LINE - Ligne de marchandise' },
      { value: 'GOODS', label: 'GOODS - Marchandises (par catégorie)' },
      { value: 'VALUE_BRACKET', label: 'VALUE_BRACKET - Tranche de valeur' }
    ];

    const priorityOptions = [
      { value: 1, label: 'Très élevée (1)' },
      { value: 10, label: 'Élevée (10)' },
      { value: 50, label: 'Moyenne (50)' },
      { value: 100, label: 'Normale (100)' },
      { value: 200, label: 'Basse (200)' }
    ];

    const currencyOptions = [
      { value: 'XOF', label: 'XOF - Franc CFA' },
      { value: 'EUR', label: 'EUR - Euro' },
      { value: 'USD', label: 'USD - Dollar américain' },
      { value: 'GBP', label: 'GBP - Livre sterling' }
    ];

    const regionOptions = [
      'AFRIQUE',
      'EUROPE',
      'ASIE',
      'AMERIQUE',
      'OCEANIE'
    ];

    interface PricingRuleForm {
      code: string;
      name: string;
      description: string;
      tariffZone: { value: string, label: string } | null;
      appliesToEntityType: { value: string, label: string } | null;
      categorieProduit: { value: string, label: string } | null;
      regimedouanier: { value: string, label: string } | null;
      amountCurrency: { value: string, label: string } | null;
      priority: { value: number, label: string } | null;
      baseAmount: number | null;
      perContainerAmount: number | null;
      percentageRate: number | null;
      minDeclaredValue: number | null;
      maxDeclaredValue: number | null;
      minFeeAmount: number | null;
      maxFeeAmount: number | null;
      isExemptionRule: boolean;
      exemptionRate: number | null;
      isActive: boolean;
      effectiveFrom: string;
      effectiveTo: string;
      containerTypes: { value: string, label: string } | null;
      requireDistantOrigin: boolean;
      loadingPortRegion: { value: string, label: string } | null;
      packagingType: { value: string, label: string } | null;
      trancheSize: number | null;
      unitOfMeasure: { value: string, label: string } | null;
      minWeight: number | null;
      maxWeight: number | null;
      maxPerEctn: number | null;
      amountForLocalOrigin: number | null;
      amountForDistantOrigin: number | null;
    }

    const formData = reactive<PricingRuleForm>({
      code: "",
      name: "",
      description: "",
      tariffZone: null,
      appliesToEntityType: null,
      categorieProduit: null,
      regimedouanier: null,
      amountCurrency: null,
      priority: null,
      baseAmount: null,
      perContainerAmount: null,
      percentageRate: null,
      minDeclaredValue: null,
      maxDeclaredValue: null,
      minFeeAmount: null,
      maxFeeAmount: null,
      isExemptionRule: false,
      exemptionRate: null,
      isActive: true,
      effectiveFrom: "",
      effectiveTo: "",
      containerTypes: null,
      requireDistantOrigin: false,
      loadingPortRegion: null,
      packagingType: null,
      trancheSize: null,
      unitOfMeasure: null,
      minWeight: null,
      maxWeight: null,
      maxPerEctn: null,
      amountForLocalOrigin: null,
      amountForDistantOrigin: null,
    });

    const fetchTariffZones = async () => {
      try {
        const response = await ApiService.get('/tariff-zones?limit=0');
        const zoneData = response.data.data.data;
        tariffZoneOptions.value = zoneData.map((zone) => ({
          value: zone.id,
          label: `${zone.code} - ${zone.name}`,
        }));
      } catch (err) {
        console.error('Erreur chargement zones tarifaires:', err);
      }
    };

    const fetchCategProduit = async () => {
      try {
        const response = await ApiService.get('/categProduits?limit=0');
        const categData = response.data.data.data;
        categorieProduitOptions.value = categData.map((categProduit) => ({
          value: categProduit.id,
          label: `${categProduit.name}`
        }));
      } catch (err) {
        console.error('Erreur chargement catégories:', err);
      }
    };

    const fetchRegime = async () => {
      try {
        const response = await ApiService.get('/regimes?limit=0');
        const regimeData = response.data.data.data;
        regimeDouanierOptions.value = regimeData.map((regimeDouanier) => ({
          value: regimeDouanier.id,
          label: `${regimeDouanier.name}`,
        }));
      } catch (err) {
        console.error('Erreur chargement régimes:', err);
      }
    };

    const fetchContainerTypes = async () => {
      try {
        const response = await ApiService.get('/type-containers?limit=0');
        const typesData = response.data.data.data;
        containerTypeOptions.value = typesData.map((typeContainer) => ({
          value: typeContainer.id,
          label: `${typeContainer.name}`
        }));
      } catch (err) {
        console.error('Erreur chargement types de conteneurs:', err);
      }
    };

    const getPricingRule = async (id: string) => {
      try {
        console.log('========== CHARGEMENT DE LA RÈGLE ==========');
        console.log('1. ID de la règle:', id);

        const response = await ApiService.get(`/pricing-rule/${id}`);
        const rule = response.data.data;

        console.log('2. Données brutes reçues du serveur:', response.data.data);

        formData.code = rule.code;
        formData.name = rule.name;
        formData.description = rule.description || "";

        formData.tariffZone = rule.tariffZone ?
          {
            value: rule.tariffZone.id,
            label: `${rule.tariffZone.code} ${rule.tariffZone.name}`
          } : null;

        // Type d'entité → objet complet
        const entityTypeObj = entityTypeOptions.find((opt: any) => opt.value === rule.appliesToEntityType);
        formData.appliesToEntityType = entityTypeObj || null;
        selectedEntityType.value = rule.appliesToEntityType || null;
        
        console.log('3. selectedEntityType assigné:', selectedEntityType.value);
        console.log('4. entityTypeObj trouvé:', entityTypeObj);

        // Catégorie produit → objet complet
        formData.categorieProduit = rule.categorieProduit ? {
          value: rule.categorieProduit.id,
          label: rule.categorieProduit.name
        } : null;

        // Régime douanier → objet complet
        formData.regimedouanier = rule.regimedouanier ? {
          value: rule.regimedouanier.id,
          label: rule.regimedouanier.name
        } : null;

        // Devise → objet complet
        const currencyObj = currencyOptions.find(opt => opt.value === rule.amountCurrency);
        formData.amountCurrency = currencyObj || null;

        // Priorité → objet complet
        const priorityObj = priorityOptions.find(opt => opt.value === rule.priority);
        formData.priority = priorityObj || null;

        // Montants
        formData.baseAmount = rule.baseAmount;
        formData.perContainerAmount = rule.perContainerAmount;
        formData.percentageRate = rule.percentageRate;
        formData.minDeclaredValue = rule.minDeclaredValue;
        formData.maxDeclaredValue = rule.maxDeclaredValue;
        formData.minFeeAmount = rule.minFeeAmount;
        formData.maxFeeAmount = rule.maxFeeAmount;

        // Type de conditionnement → objet complet
        const packagingObj = packagingTypeOptions.find((opt: any) => opt.value === rule.packagingType);
        formData.packagingType = packagingObj || null;
        selectedPackagingType.value = rule.packagingType || null;
        
        formData.trancheSize = rule.trancheSize;
        
        const unitObj = unitOfMeasureOptions.find((opt: any) => opt.value === rule.unitOfMeasure);
        formData.unitOfMeasure = unitObj || null;
        formData.minWeight = rule.minWeight;
        formData.maxWeight = rule.maxWeight;
        formData.maxPerEctn = rule.maxPerEctn;

        formData.isExemptionRule = rule.isExemptionRule || false;
        formData.exemptionRate = rule.exemptionRate;
        formData.isActive = rule.isActive !== undefined ? rule.isActive : true;

        // Dates
        formData.effectiveFrom = rule.effectiveFrom ? rule.effectiveFrom.split('T')[0] : "";
        formData.effectiveTo = rule.effectiveTo ? rule.effectiveTo.split('T')[0] : "";

        // Métadonnées
        if (rule.metadata) {
          try {
            const metadata = typeof rule.metadata === 'string' ? JSON.parse(rule.metadata) : rule.metadata;

            if (metadata.tariffs) {
              formData.amountForLocalOrigin = metadata.tariffs.localOrigins;
              formData.amountForDistantOrigin = metadata.tariffs.distantOrigins;
            }

            // containerTypes → objets complets
            if (metadata.containerTypes && Array.isArray(metadata.containerTypes)) {
              formData.containerTypes = metadata.containerTypes.map((ctId: string) => {
                const ctObj = containerTypeOptions.value.find(opt => opt.value === ctId);
                return ctObj || { value: ctId, label: ctId };
              });
            } else {
              formData.containerTypes = null;
            }

            formData.requireDistantOrigin = metadata.requireDistantOrigin || false;
            formData.loadingPortRegion = metadata.loadingPortRegion || [];
          } catch (e) {
            console.error('Erreur parsing metadata:', e);
            formData.containerTypes = null;
            formData.requireDistantOrigin = false;
            formData.loadingPortRegion = null;
          }
        } else {
          formData.containerTypes = null;
          formData.requireDistantOrigin = false;
          formData.loadingPortRegion = null;
        }

        console.log('FormData final après chargement:', JSON.parse(JSON.stringify(formData)));
        console.log('========== FIN CHARGEMENT ==========');

        // Plus besoin de forcer la réactivité → tout est brut et réactif nativement
        await nextTick();
        onEntityTypeChange({ value: formData.appliesToEntityType }); 

      } catch (err: any) {
        console.error('ERREUR getPricingRule:', err);
        error(err.response?.data?.message || "Erreur lors du chargement");
        router.push({ name: "ListeBescPricingRulePage" });
      }
    };

    const editPricingRule = async (values: any) => {
      const payload = {
        code: values.code || formData.code,
        name: values.name || formData.name,
        description: values.description || formData.description,
        appliesToEntityType: formData.appliesToEntityType?.value || formData.appliesToEntityType,
        tariffZone: formData.tariffZone?.value || formData.tariffZone,
        amountCurrency: formData.amountCurrency?.value || formData.amountCurrency,
        categorieProduit: formData.categorieProduit?.value || formData.categorieProduit,
        regimedouanier: formData.regimedouanier?.value || formData.regimedouanier,
        priority: formData.priority?.value || formData.priority,
        baseAmount: formData.baseAmount,
        perContainerAmount: formData.perContainerAmount,
        percentageRate: formData.percentageRate,
        minDeclaredValue: formData.minDeclaredValue,
        maxDeclaredValue: formData.maxDeclaredValue,
        minFeeAmount: formData.minFeeAmount,
        maxFeeAmount: formData.maxFeeAmount,
        isExemptionRule: formData.isExemptionRule,
        exemptionRate: formData.exemptionRate,
        isActive: formData.isActive,
        effectiveFrom: formData.effectiveFrom || null,
        effectiveTo: formData.effectiveTo || null,
        packagingType: formData.packagingType?.value || formData.packagingType,
        trancheSize: formData.trancheSize,
        unitOfMeasure: formData.unitOfMeasure?.value || formData.unitOfMeasure,
        minWeight: formData.minWeight,
        maxWeight: formData.maxWeight,
        maxPerEctn: formData.maxPerEctn,
        amountForLocalOrigin: formData.amountForLocalOrigin,
        amountForDistantOrigin: formData.amountForDistantOrigin,
        metadata: JSON.stringify({
          containerTypes: Array.isArray(formData.containerTypes) 
            ? formData.containerTypes.map((ct: any) => ct?.value || ct)
            : [],
          loadingPortRegion: formData.loadingPortRegion || [],
          requireDistantOrigin: formData.requireDistantOrigin || false
        })
      };

      console.log(' Payload Edit envoyé:', payload);
      try {
        const { data } = await ApiService.put(`/pricing-rule/${ruleId}`, payload);
        if (data.code == 200) {
          success(data.message);
          router.push({ name: "ListeBescPricingRulePage" });
        }
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la modification");
      }
    };

    onMounted(async () => {
      console.log('========== MONTAGE DU COMPOSANT ==========');

      console.log('Chargement des options...');
      await fetchTariffZones();
      console.log('✓ Zones tarifaires chargées:', tariffZoneOptions.value.length);

      await fetchCategProduit();
      console.log('✓ Catégories produits chargées:', categorieProduitOptions.value.length);

      await fetchRegime();
      console.log('✓ Régimes chargés:', regimeDouanierOptions.value.length);

      await fetchContainerTypes();
      console.log('✓ Types conteneurs chargés:', containerTypeOptions.value.length);

      console.log('Chargement de la règle ID:', ruleId);
      await getPricingRule(ruleId);

      console.log('========== FIN MONTAGE ==========');
    });

    return {
      formData,
      editPricingRule,
      pricingRuleSchema,
      pricingRuleForm,
      selectedEntityType,
      entityTypeHelp,
      onEntityTypeChange,
      tariffZoneOptions,
      categorieProduitOptions,
      regimeDouanierOptions,
      entityTypeOptions,
      priorityOptions,
      currencyOptions,
      regionOptions,
      containerTypeOptions,
      selectedPackagingType,
      packagingTypeOptions,
      unitOfMeasureOptions,
      onPackagingTypeChange
    };
  },
});
</script>
